import React, { useMemo } from 'react';
import { Badge, Button, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Envelope, FileEarmark, Files, Pen, Phone, SlashCircle, Trash } from 'react-bootstrap-icons';
import { canViewCollectedFees, isAdmin, isClient, isStaff } from '../../../../helpers/global';
import { getBgColorFromStatus } from '../../../../helpers/ui';
import useAppChoices from '../../../../hooks/useAppChoices';
import CircularProgressBar from '../../../circular-progress';
import SmallDropdownToggle from '../../../SmallDropdownToggle';
import {
  CLIENT_CANCELLED_INTERNAL_STATUSES,
  ORDER_TYPES,
  getClientFacingOrderStatus
} from '../../../../helpers/constants';
import Accordion from '../../../common/Accordion';
import { Calendar, Circle, DollarSign, Mail, User } from 'lucide-react';
import EtaClickableValue from '../EtaClickableValue';
import OrderDetailField from '../OrderDetailField';
import OrderPinToggle from '../OrderPinToggle';
import RushIconSVG from '../../../icons/RushIconSVG';
import CancellationRequestedBadge from '../CancellationRequestedBadge';

const commonRows = [
  {
    label: 'Assigned Processor',
    key: 'assignedProcessors',
    isArray: true,
    objectKey: 'name',
    icon: <User className="icon-sm" />
  },
  { label: 'Hard Cost', key: 'hardCost', icon: <DollarSign className="icon-sm" /> },
  { label: 'Processing Fee', key: 'processingFee', icon: <DollarSign className="icon-sm" /> },
  { label: 'Assigned Surveyor', key: 'assignedSurveyor', icon: <User className="icon-sm" /> },
  { label: 'Collected Fee', key: 'collectedFees', icon: <DollarSign className="icon-sm" /> }
];

const certificationFields = [
  { label: 'Title Certification', key: 'titleCertification', icon: <FileEarmark className="icon-sm" /> },
  { label: 'Buyer Certification', key: 'buyerCertification', icon: <FileEarmark className="icon-sm" /> },
  { label: 'Lender Certification', key: 'lenderCertification', icon: <FileEarmark className="icon-sm" /> },
  {
    label: 'Underwriter Certification',
    key: 'underwriterCertification',
    icon: <FileEarmark className="icon-sm" />
  }
];

const accessInformationFields = [
  { label: 'Listing Agent Name', key: 'listingAgentName', icon: <User className="icon-sm" /> },
  { label: 'Listing Agent Phone', key: 'listingAgentPhone', icon: <Phone className="icon-sm" /> }
];

const commonRowsClient = [
  {
    label: 'Assigned Processor',
    key: 'assignedProcessors',
    isArray: true,
    objectKey: 'name',
    icon: <User className="icon-sm" />
  },
  {
    label: 'Processor Email',
    key: 'assignedProcessorEmails',
    isArray: true,
    objectKey: 'email',
    icon: <Mail className="icon-sm" />
  },
  { label: 'Hard Copy', key: 'hardCopy', icon: <Files size={14} className="mr-2" /> },
  { label: 'Listing Agent Name', key: 'listingAgentName', icon: <User className="icon-sm" /> },
  { label: 'Listing Agent Phone', key: 'listingAgentPhone', icon: <Phone size={14} className="mr-2" /> }
];

const priceFields = ['hardCost', 'totalCost', 'processingFee', 'invoiceAmount', 'collectedFees'];
const dateFields = ['dateDelivered', 'neededDate', 'createdAt', 'estimatedTurnAroundTime'];

const HARD_COST_REQUIRED_STATUSES = ['Information Requested', 'Ready for QA'];

const SurveyDetails = ({
  survey: originalSurvey,
  disabled,
  onStatusToggle,
  onDeleteClick,
  onRequestCancellationClick,
  onUndoCancellationClick,
  onPaymentReminderClicked,
  hasPastDueUnpaidInvoices = false,
  serviceCreation,
  onEtaUpdate,
  onSyncOrderHardcost,
  loadingMetaData,
  onOrderFieldUpdate,
  service,
  onTogglePin,
  isPinning = false
}) => {
  const role = useMemo(() => localStorage.getItem('user-role'), []);
  const currentUserInfo = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const canSeeCollectedFees = useMemo(() => canViewCollectedFees(currentUserInfo), [currentUserInfo]);
  const statuses = useAppChoices('Survey Status');

  const survey = useMemo(
    () => ({
      ...originalSurvey,
      totalCost: ['hardCost', 'processingFee'].reduce((acc, field) => acc + (originalSurvey[field] || 0), 0),
      collectedFees: (originalSurvey.invoiceAmount || 0) - (originalSurvey.hardCost || 0),
      assignedProcessorEmails: originalSurvey.assignedProcessors || []
    }),
    [originalSurvey]
  );

  const showPaymentReminderButton = () =>
    isAdmin(role) && survey['status'] === 'Pending To Pay' && hasPastDueUnpaidInvoices;

  const fields = useMemo(() => {
    const baseRows = !isStaff(role)
      ? commonRowsClient
      : canSeeCollectedFees
        ? commonRows
        : commonRows.filter(r => r.key !== 'collectedFees');

    if (isClient(role) && survey.assignedProcessors?.length > 0) return baseRows;
    return baseRows.filter(r => r.key !== 'assignedProcessorEmails');
  }, [role, canSeeCollectedFees, survey.assignedProcessors]);

  const orderDetailFieldProps = {
    order: survey,
    dateFields,
    priceFields,
    hardCostRequiredStatuses: HARD_COST_REQUIRED_STATUSES,
    formatBooleans: true,
    role,
    currentUserInfo,
    loadingMetaData,
    onSyncOrderHardcost,
    disabled,
    onOrderFieldUpdate,
    allowInlineEditing: !serviceCreation
  };

  const renderFieldRows = (fieldList, rowKeyPrefix) => {
    const rows = [];
    for (let i = 0; i < fieldList.length; i += 4) {
      const slice = fieldList.slice(i, i + 4);
      rows.push(
        <div key={`${rowKeyPrefix}-${i}`} className={`row ${i === 0 ? 'mt-1' : 'mt-4'}`}>
          {slice.map((field, index) => (
            <OrderDetailField
              key={field.key ?? `${rowKeyPrefix}-${i}-${index}`}
              field={field}
              {...orderDetailFieldProps}
            />
          ))}
        </div>
      );
    }
    return rows;
  };

  return (
    <div className="card p-4 shadow-sm h-100">
      <div className="d-flex justify-content-between align-items-center flex-wrap">
        <div className="mb-2 mb-md-0">
          <div className="d-flex align-items-center">
            <OrderPinToggle
              order={originalSurvey}
              disabled={disabled}
              isPinning={isPinning}
              onTogglePin={onTogglePin}
            />
            <h1 className="font-weight-semibold xlargeFont mb-0">Survey </h1>
            <div className="d-flex align-items-center ml-2" style={{ gap: '8px' }}>
              {survey?.rush && (
                <span
                  style={{
                    padding: '1px 6px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                  className="text-danger bg-danger-light rounded-pill"
                >
                  RUSH
                </span>
              )}
              {survey['cancellationRequested'] && (
                <CancellationRequestedBadge
                  disabled={disabled}
                  hideUndo={CLIENT_CANCELLED_INTERNAL_STATUSES.includes(survey['status'])}
                  onUndoClick={() => onUndoCancellationClick?.(survey)}
                />
              )}
            </div>
          </div>
          <div className="d-flex align-items-center text-muted midFont mt-1">Service No. {survey['accurateId']}</div>
        </div>
        <div>
          {!serviceCreation && showPaymentReminderButton() && (
            <Button
              variant="outline-dark"
              disabled={disabled}
              className="ml-1 d-inline-flex align-items-center shadow-sm"
              onClick={() => onPaymentReminderClicked(survey?._id)}
            >
              <Envelope size={13} className="mr-1" />
              Send Payment Reminder
            </Button>
          )}
          {!isClient(role) && (
            <>
              <Button
                variant="outline-success"
                disabled={disabled}
                className="ml-1 d-inline-flex align-items-center shadow-sm"
                onClick={() =>
                  window.open(
                    `/services/pipeline?serviceId=${service._id}&orderId=${survey._id}&reportType=${ORDER_TYPES.SURVEY}`,
                    '_blank'
                  )
                }
              >
                <FileEarmark size={13} className="mr-1" />
                Open Report Builder
              </Button>
              <Button
                variant="outline-danger"
                className="ml-1 d-inline-flex align-items-center shadow-sm"
                onClick={() => onDeleteClick(survey)}
                disabled={disabled}
              >
                <Trash size={13} className="mr-1" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Details Section */}
      <div>
        {(() => {
          const allFields = [
            {
              label: 'Status',
              key: 'status',
              icon: <Circle fill="currentColor" className="icon-sm" />,
              isSpecial: true,
              render: () =>
                isStaff(role) ? (
                  !serviceCreation && (
                    <Dropdown onClick={e => e.stopPropagation()} className="d-inline-block mr-1">
                      <OverlayTrigger
                        placement="bottom"
                        overlay={<Tooltip id={`tooltip-order-status-action`}>Change order status</Tooltip>}
                      >
                        <SmallDropdownToggle
                          variant=""
                          disabled={disabled}
                          style={{ border: 0, backgroundColor: getBgColorFromStatus(survey['status']).bg }}
                          size="sm"
                          aria-label="Change order status"
                        >
                          <span
                            className="d-inline-flex align-items-center largeFont font-weight-semibold p-1"
                            style={{ color: getBgColorFromStatus(survey['status']).text }}
                          >
                            {loadingMetaData?.status && <CircularProgressBar size={1} className="mr-2" />}
                            {survey['status']}
                          </span>
                        </SmallDropdownToggle>
                      </OverlayTrigger>

                      <Dropdown.Menu>
                        {statuses
                          ?.filter(s => s !== 'Pending')
                          .map(s => (
                            <Dropdown.Item
                              style={{
                                backgroundColor: getBgColorFromStatus(s).bg,
                                color: getBgColorFromStatus(s).text
                              }}
                              key={s}
                              onClick={() => onStatusToggle && onStatusToggle(s)}
                            >
                              {s}
                            </Dropdown.Item>
                          ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  )
                ) : (
                  (() => {
                    const clientStatus = getClientFacingOrderStatus(
                      survey['status'],
                      survey['cancellationRequested']
                    );
                    const statusColors = getBgColorFromStatus(clientStatus);
                    return (
                      <Badge className="mr-1 p-2" style={{ backgroundColor: statusColors.bg }}>
                        <span className="midFont" style={{ color: statusColors.text }}>
                          {clientStatus === 'Completed/Unpaid' ? (
                            <>
                              Completed/<span className="text-danger">Unpaid</span>
                            </>
                          ) : (
                            clientStatus
                          )}
                        </span>
                      </Badge>
                    );
                  })()
                )
            },
            {
              label: 'ETA',
              key: 'estimatedTurnAroundTime',
              icon: <Calendar className="icon-sm" />,
              labelWarningTooltipText:
                survey?.status === 'Update' && !survey?.isEtaUpdatedAfterUpdateStatus
                  ? 'New ETA is required'
                  : undefined,
              isSpecial: true,
              render: () => (
                <EtaClickableValue
                  value={survey['estimatedTurnAroundTime']}
                  disabled={disabled}
                  onDateChange={onEtaUpdate}
                  needsNewEta={survey?.status === 'Update' && !survey?.isEtaUpdatedAfterUpdateStatus}
                  loading={loadingMetaData?.eta}
                />
              )
            },
            ...(!isClient(role)
              ? [
                  {
                    label: 'Rush',
                    key: 'rush',
                    icon: <RushIconSVG width="18px" height="18px" />
                  }
                ]
              : []),
            {
              label: 'Date Ordered',
              key: 'createdAt',
              icon: <Calendar className="icon-sm" />
            },
            ...(survey['dateDelivered']
              ? [
                  {
                    label: 'Date Delivered',
                    key: 'dateDelivered',
                    icon: <Calendar className="icon-sm" />
                  }
                ]
              : []),
            ...fields.filter(field => !['listingAgentName', 'listingAgentPhone'].includes(field.key))
          ];

          // Render all fields in rows of 4
          const rows = [];
          for (let i = 0; i < allFields.length; i += 4) {
            const rowFields = allFields.slice(i, i + 4);
            rows.push(
              <div key={i} className="row mt-4">
                {rowFields.map((field, index) => (
                  <OrderDetailField key={field.key ?? `${i}-${index}`} field={field} {...orderDetailFieldProps} />
                ))}
              </div>
            );
          }

          return rows;
        })()}
      </div>

      <div className="p-2 px-3 mt-4 card bg-muted">
        <Accordion title={<span className="font-weight-semibold largeFont">Access Information:</span>}>
          {renderFieldRows(accessInformationFields, 'access')}
        </Accordion>
      </div>
      <div className="p-2 px-3 mt-2 mb-1 card bg-muted">
        <Accordion title={<span className="font-weight-semibold largeFont">Certifications:</span>}>
          {renderFieldRows(certificationFields, 'cert')}
        </Accordion>
      </div>

      {isClient(role) &&
        !survey?.['cancellationRequested'] &&
        !CLIENT_CANCELLED_INTERNAL_STATUSES.includes(survey?.status) && (
        <div className="mt-3 d-flex justify-content-end">
          <Button
            variant="outline-danger"
            className="ml-1 d-inline-flex align-items-center shadow-sm"
            onClick={() => onRequestCancellationClick(survey)}
            disabled={disabled}
          >
            <SlashCircle size={13} className="mr-1" />
            Request Order Cancellation
          </Button>
        </div>
      )}
    </div>
  );
};

export default SurveyDetails;
